import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import { ApplicationStatus } from '@hirenfire/shared';
import type { ApiResponse } from '@hirenfire/shared';

interface Application {
  id: string;
  jobId: string;
  jobTitle: string;
  agencyName: string;
  status: ApplicationStatus;
  coverLetter: string;
  createdAt: string;
  budget: number;
  currency: string;
}

const statusConfig: Record<ApplicationStatus, { variant: 'warning' | 'success' | 'error' | 'default'; icon: React.ReactNode }> = {
  [ApplicationStatus.PENDING]: { variant: 'warning', icon: <Clock className="w-4 h-4" /> },
  [ApplicationStatus.ACCEPTED]: { variant: 'success', icon: <CheckCircle className="w-4 h-4" /> },
  [ApplicationStatus.REJECTED]: { variant: 'error', icon: <XCircle className="w-4 h-4" /> },
  [ApplicationStatus.WITHDRAWN]: { variant: 'default', icon: <XCircle className="w-4 h-4" /> },
};

export function MyApplicationsPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<ApplicationStatus | 'ALL'>('ALL');

  useEffect(() => {
    async function fetchApplications() {
      try {
        const params = activeFilter !== 'ALL' ? `?status=${activeFilter}` : '';
        const res = await api.get<ApiResponse<Application[]>>(`/applications${params}`);
        if (res.data.data) setApplications(res.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchApplications();
  }, [activeFilter]);

  const filters: Array<{ label: string; value: ApplicationStatus | 'ALL' }> = [
    { label: 'All', value: 'ALL' },
    { label: 'Pending', value: ApplicationStatus.PENDING },
    { label: 'Accepted', value: ApplicationStatus.ACCEPTED },
    { label: 'Rejected', value: ApplicationStatus.REJECTED },
    { label: 'Withdrawn', value: ApplicationStatus.WITHDRAWN },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">My Applications</h1>
        <p className="text-gray-400 mt-1">Track the status of your job applications</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => (
          <Button
            key={filter.value}
            variant={activeFilter === filter.value ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setActiveFilter(filter.value);
              setIsLoading(true);
            }}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : applications.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No applications found</p>
            <p className="text-gray-600 text-sm mt-2">
              Start by browsing available jobs
            </p>
            <Link to="/jobs">
              <Button variant="primary" className="mt-4">
                Browse Jobs
              </Button>
            </Link>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {applications.map((app) => {
            const config = statusConfig[app.status];
            return (
              <Card key={app.id} className="hover:border-dark-500 transition-all">
                <CardBody>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/jobs/${app.jobId}`}
                        className="font-semibold text-white hover:text-primary-400 transition-colors"
                      >
                        {app.jobTitle}
                      </Link>
                      <p className="text-sm text-gray-400 mt-0.5">{app.agencyName}</p>
                      {app.coverLetter && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {app.coverLetter}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      <Badge variant={config.variant}>
                        <span className="flex items-center gap-1">
                          {config.icon}
                          {app.status}
                        </span>
                      </Badge>
                      <span className="text-sm font-semibold text-success-400">
                        ${app.budget} {app.currency}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(app.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
